/**
 * StandKrokisi.jsx — Duvarlar adımında standın GERÇEK ana hattı (backlog #676).
 *
 * ESKİSİ: adımda çizim yoktu (satır listesi) ya da her stand için aynı sabit L şekli vardı. ŞİMDİ: çizimin ittiği
 * köşelerden kurulan ana hat (standKroki.js → cizimKonturu) çizilir; bir kenara TIKLAMAK o kenarın satırını seçer.
 * Duvarı olan kenarlar satırlarındaki numarayla işaretlenir (ekranda gördüğünüz numara ile backend mesajındaki numara
 * hep aynıdır, bkz. duvarSatirlari.numaralar), duvarı olmayan (işareti kaldırılmış) kenarlar kesik gri çizilir,
 * çizimin koridora baktığını söylediği kenarlar mavi çizilir.
 *
 * Kontrollü bir bileşendir: seçili satır ve tıklama üstten gelir (CizilenDuvarlarFormu). Yalnızca `kontur` çizilebilirse
 * kullanılır; null ise çağıran taraf bunu hiç oluşturmaz (satır listesi kalır).
 */
import React from "react";
import { useTranslation } from "react-i18next";

const RENK = {
  cizgi: "#178f8f",
  koridor: "#0b7fc2",
  yok: "#adb5bd",
  secili: "#fd7e14",
  dolgu: "#178f8f",
};

/**
 * @param {object}   kontur   cizimKonturu() sonucu (null olamaz)
 * @param {object[]} satirlar cizilenSatirlar() satırları ({key, dahil, koridor, ...})
 * @param {object}   numara   satır anahtarı → görünen duvar numarası (duvarSatirlari.numaralar); dahil olmayanın yoktur
 * @param {string}   secili   seçili satırın anahtarı | null
 * @param {Function} onSecim  (anahtar) => void
 * @param {number}   maxGenislik px üst sınır
 */
export default function StandKrokisi({ kontur, satirlar, numara, secili, onSecim, maxGenislik = 380 }) {
  const { t } = useTranslation("portal");
  const vb = kontur.viewBox;
  const buyuk = Math.max(vb.w, vb.h);
  const yaziBoyu = buyuk * 0.05;
  const satirOf = new Map((satirlar ?? []).map((s) => [s.key, s]));
  const cokSekil = kontur.shapes.length > 1;

  const kenarlar = kontur.shapes.flatMap((s) => s.sides);
  // seçili kenar en üstte çizilsin: sona al
  const sirali = [...kenarlar.filter((k) => k.key !== secili), ...kenarlar.filter((k) => k.key === secili)];

  function sec(k) {
    onSecim?.(k.key);
  }

  return (
    <svg
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      role="group" aria-label={t("projeler.krokiSvgEtiketi")}
      style={{ width: "100%", maxWidth: maxGenislik, aspectRatio: `${vb.w} / ${vb.h}`, display: "block" }}
    >
      {kontur.shapes.map((s) => (
        <polygon
          key={`d${s.shapeId}`} points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill={RENK.dolgu} fillOpacity="0.1" stroke="none"
        />
      ))}

      {sirali.map((k) => {
        const satir = satirOf.get(k.key);
        const dahil = satir ? satir.dahil : true;
        const aktif = k.key === secili;
        const renk = aktif ? RENK.secili : (!dahil ? RENK.yok : (k.facesCorridor ? RENK.koridor : RENK.cizgi));
        const n = numara?.[k.key];
        return (
          <g key={k.key}>
            <line
              x1={k.a.x} y1={k.a.y} x2={k.b.x} y2={k.b.y}
              stroke={renk} strokeWidth={aktif ? 5 : 3} vectorEffect="non-scaling-stroke"
              strokeDasharray={dahil ? undefined : "5 4"} strokeLinecap="round"
            />
            {/* görünmez, kalın tıklama alanı: ince bir çizgiye isabet etmek zordu */}
            <line
              data-kenar={k.key}
              x1={k.a.x} y1={k.a.y} x2={k.b.x} y2={k.b.y}
              stroke="transparent" strokeWidth={16} vectorEffect="non-scaling-stroke" strokeLinecap="round"
              role="button" tabIndex={0} aria-pressed={aktif}
              aria-label={t("projeler.krokiKenarSec", { n: k.edgeIndex + 1 })}
              style={{ cursor: "pointer", outline: "none" }}
              onClick={() => sec(k)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); sec(k); } }}
            />
            {dahil && n != null && (
              <text
                x={k.label.x} y={k.label.y} textAnchor="middle" dominantBaseline="central"
                fontSize={yaziBoyu} fontWeight={aktif ? 700 : 600} fill={aktif ? RENK.secili : "#343a40"}
                stroke="#fff" strokeWidth={yaziBoyu * 0.3} paintOrder="stroke" style={{ pointerEvents: "none" }}
              >
                {n}
              </text>
            )}
          </g>
        );
      })}

      {cokSekil && kontur.shapes.map((s) => (
        <text
          key={`n${s.shapeId}`} x={s.centroid.x} y={s.centroid.y} textAnchor="middle" dominantBaseline="central"
          fontSize={yaziBoyu * 0.85} fill="#6c757d" style={{ pointerEvents: "none" }}
        >
          {t("projeler.sekilNumarasi", { n: s.shapeNo })}
        </text>
      ))}
    </svg>
  );
}
